import { Box } from 'designSystem/components/Box'
import { SensorPreviewPanel } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/sensors/SensorPreviewPanel'
import { SensorResultsSection } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/sensors/SensorResultsSection'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { useDocumentSearchData } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import { useAgenticSearchHeaderState } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useAgenticSearchHeaderState'
import { useDocumentResultsKeyboardNavigation } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsKeyboardNavigation'
import { useSensorResultsInteractions } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useSensorResultsInteractions'
import { PageContainer } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/advancedSearchStyles'
import { isAgenticDocumentSearchResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { isAiDocumentSearchEngine } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchMode'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import React from 'react'

import { DocumentResultsSection } from './DocumentResultsSection'
import { UnifiedSearchHeader } from './UnifiedSearchHeader'

function ResultsLayoutBox(props: React.ComponentProps<typeof Box>) {
  return (
    <Box
      display='flex'
      direction='column'
      flex='one'
      minHeight='none'
      overflow='hidden'
      {...props}
    />
  )
}

export const UnifiedSearchResults: React.FC = () => {
  const {
    onOpenChatbot,
    onOpenSensorDashboard,
    onAddSensorToExistingTab,
    openTabs,
  } = useSearchActions()
  const {
    isLoadingDocuments,
    sensorResults,
    sensorTotalCount,
    isLoadingSensors,
    rawSearchData,
    searchEngine,
  } = useDocumentSearchData()

  const {
    siteId,
    searchState,
    searchInput,
    handleSearchChange,
    handleSearchKeyDown,
    handleSearchSubmit,
    contentFilter,
    searchMode,
    viewMode,
    handleViewModeChange,
    isLinkedDocsMode,
    isActive,
    searchSuggestions,
    onSuggestionSelect,
    onClearSearchHistory,
    favoriteSearches,
    isSearchFavorited,
    toggleSearchFavorite,
    onSearchStateChange,
  } = useAdvancedSearchUX()

  const isAgenticSearchExperience =
    isAiDocumentSearchEngine(searchEngine) && contentFilter === 'documents'
  const agenticSearchResult = isAgenticDocumentSearchResult(rawSearchData)
    ? rawSearchData
    : undefined
  const agenticConversation =
    searchState.agenticSearch?.siteId === siteId
      ? searchState.agenticSearch.conversation
      : undefined
  const {
    turns: agenticTurns,
    onAgenticSearchSubmit,
    onAgenticSearchKeyDown,
  } = useAgenticSearchHeaderState({
    conversation: agenticConversation,
    result: agenticSearchResult,
    isLoadingDocuments,
    query: searchState.query,
    siteId,
    searchMode,
    onSearchSubmit: handleSearchSubmit,
    onSearchKeyDown: handleSearchKeyDown,
  })

  const {
    selectedSensor,
    handleSensorClick,
    handleSensorDoubleClick,
    handleCloseSensorPreview,
    handleSensorAddToContext,
  } = useSensorResultsInteractions({
    onOpenChatbot,
    onOpenSensorDashboard,
    onSearchStateChange,
  })

  useDocumentResultsKeyboardNavigation({
    enabled: isActive && contentFilter === 'sensors',
    handleSensorClick,
    handleSensorDoubleClick,
    mode: 'sensors',
    selectedSensor,
    sensorResults,
  })

  return (
    <PageContainer>
      <ResultsLayoutBox>
        {contentFilter === 'documents' ? (
          <DocumentResultsSection
            isLinkedDocsMode={isLinkedDocsMode}
            isAgenticSearchExperience={isAgenticSearchExperience}
            contentFilter={contentFilter}
            searchInput={searchInput}
            onSearchChange={handleSearchChange}
            onSearchKeyDown={handleSearchKeyDown}
            onSearchSubmit={handleSearchSubmit}
            viewMode={viewMode}
            suggestions={searchSuggestions}
            onSuggestionSelect={onSuggestionSelect}
            onClearHistory={onClearSearchHistory}
            favoriteSearches={favoriteSearches}
            isSearchFavorited={isSearchFavorited}
            onToggleSearchFavorite={toggleSearchFavorite}
            agenticTurns={agenticTurns}
            onAgenticSearchKeyDown={onAgenticSearchKeyDown}
            onAgenticSearchSubmit={onAgenticSearchSubmit}
          />
        ) : (
          <UnifiedSearchHeader
            isLinkedDocsMode={isLinkedDocsMode}
            isAgenticSearchExperience={isAgenticSearchExperience}
            contentFilter={contentFilter}
            searchInput={searchInput}
            onSearchChange={handleSearchChange}
            onSearchKeyDown={handleSearchKeyDown}
            onSearchSubmit={handleSearchSubmit}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            suggestions={searchSuggestions}
            onSuggestionSelect={onSuggestionSelect}
            onClearHistory={onClearSearchHistory}
            favoriteSearches={favoriteSearches}
            isSearchFavorited={isSearchFavorited}
            onToggleSearchFavorite={toggleSearchFavorite}
            agenticTurns={agenticTurns}
            onAgenticSearchKeyDown={onAgenticSearchKeyDown}
            onAgenticSearchSubmit={onAgenticSearchSubmit}
          />
        )}

        {contentFilter === 'sensors' && (
          <ResultsLayoutBox>
            <SensorResultsSection
              sensors={sensorResults}
              totalCount={sensorTotalCount}
              isLoading={isLoadingSensors}
              onSensorClick={handleSensorClick}
              onSensorDoubleClick={handleSensorDoubleClick}
              selectedSensorId={selectedSensor?.id}
              fullPage
            />
          </ResultsLayoutBox>
        )}
      </ResultsLayoutBox>

      {contentFilter === 'sensors' && viewMode === 'table' && selectedSensor ? (
        <SensorPreviewPanel
          sensor={selectedSensor}
          isOpen={!!selectedSensor}
          onClose={handleCloseSensorPreview}
          onOpenDashboard={handleSensorDoubleClick}
          onOpenNewTab={onOpenSensorDashboard}
          onAddToContext={handleSensorAddToContext}
          openTabs={openTabs}
          onAddToExistingTab={onAddSensorToExistingTab}
        />
      ) : null}
    </PageContainer>
  )
}
